import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function getStatusColor(status) {
  switch (status) {
    case "delivered":
      return "success";
    case "approved":
    case "ordered":
      return "info";
    case "pending":
      return "warning";
    case "denied":
      return "error";
    default:
      return "default";
  }
}

function DeviceOrdersCard({ deviceOrders, onAddDeviceOrder, onDeleteDeviceOrder }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeOrderId, setActiveOrderId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveOrderId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveOrderId(null);
  };

  return (
    <GlassCard
      title="Equipment Orders (DME)"
      icon={<DevicesIcon color="secondary" />}
      onAdd={onAddDeviceOrder}
      addLabel="Add Equipment Order"
    >
      {deviceOrders?.length ? (
        deviceOrders.map((order) => (
          <Box
            key={order.id}
            mb={1}
            sx={{
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            data-testid={`device-order-item-${order.id}`}
            data-device-type={order.device_type}
            data-order-status={order.status}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500}>
                  {order.device_name || order.device_type}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Ordered: {order.order_date ? new Date(order.order_date).toLocaleDateString() : "N/A"}
                </Typography>
                {order.tracking_number && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Tracking: {order.tracking_number}
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={0.5} alignItems="center">
                <Chip 
                  label={order.status} 
                  size="small" 
                  color={getStatusColor(order.status)} 
                  variant="outlined"
                  data-testid={`device-order-status-chip-${order.id}`}
                  aria-label={`Equipment order status: ${order.status}`}
                />
                {onDeleteDeviceOrder && (
                  <Tooltip title="Options">
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMenuOpen(e, order.id)}
                      data-testid={`device-order-menu-button-${order.id}`}
                      aria-label={"Equipment order options"}
                      data-action="device-order-menu"
                      data-order-id={order.id}
                    >
                      <MoreVertIcon 
                        aria-label={"Equipment order options"} 
                        fontSize="small"
                        data-testid={`device-order-options-icon-${order.id}`}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No equipment orders
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeOrderId && onDeleteDeviceOrder) {
              onDeleteDeviceOrder(activeOrderId);
            }
          }}
          data-testid={`device-order-delete-menu-item-${activeOrderId}`}
          aria-label="Delete equipment order"
          data-action="delete-device-order"
          data-order-id={activeOrderId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

DeviceOrdersCard.propTypes = {
  deviceOrders: PropTypes.array,
  onAddDeviceOrder: PropTypes.func.isRequired,
  onDeleteDeviceOrder: PropTypes.func,
};

DeviceOrdersCard.defaultProps = {
  deviceOrders: [],
};

export default DeviceOrdersCard;
